import React, { useState } from 'react';
import {
  Box, Container, Paper, Typography, Card, CardContent, GridLegacy as Grid, Chip, Button, IconButton, Tooltip, Divider, Tab, Tabs, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, styled, alpha, useTheme, useMediaQuery
} from '@mui/material';
import { Star, AutoAwesome, Psychology, TrendingUp, Share, Download, Print, Info } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Types
interface PlanetPosition {
  name: string;
  sign: string;
  degree: number;
  house: number;
  retrograde: boolean;
  element: string;
  modality: string;
}

interface HouseData {
  number: number;
  sign: string;
  degree: number;
  ruler: string;
  meaning: string;
  planets: string[];
}

interface AspectData {
  planet1: string;
  planet2: string;
  aspect: string;
  degree: number;
  orb: number;
  type: 'major' | 'minor';
  nature: 'harmonious' | 'challenging' | 'neutral';
}

interface ChartData {
  personalInfo: {
    name: string;
    birthDate: string;
    birthTime: string;
    birthLocation: string;
    sunSign: string;
    moonSign: string;
    risingSign: string;
  };
  planets: PlanetPosition[];
  houses: HouseData[];
  aspects: AspectData[];
}

// Custom theme colors (using your brand palette)
const brandColors = {
  primary: {
    main: '#3B82F6',
    light: '#60A5FA',
    dark: '#1E40AF',
  },
  secondary: {
    main: '#8B5CF6',
    light: '#A78BFA',
    dark: '#6D28D9',
  },
  accent: {
    main: '#F97316',
    light: '#FB923C',
    dark: '#EA580C',
  }
};

// Styled Components
const ChartContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: `linear-gradient(135deg, 
    #030712 0%, 
    ${alpha(brandColors.primary.dark, 0.1)} 25%,
    ${alpha(brandColors.secondary.dark, 0.05)} 75%,
    #030712 100%)`,
  position: 'relative',
  overflow: 'hidden',
}));

const ChartWheel = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '400px',
  height: '400px',
  borderRadius: '50%',
  background: `linear-gradient(135deg, 
    ${alpha('#1E293B', 0.8)} 0%, 
    ${alpha(brandColors.primary.main, 0.1)} 100%)`,
  backdropFilter: 'blur(20px)',
  border: `2px solid ${alpha(brandColors.primary.main, 0.3)}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  boxShadow: `0 20px 60px ${alpha(brandColors.primary.main, 0.2)}`,
  [theme.breakpoints.down('md')]: {
    width: '300px',
    height: '300px',
  },
  [theme.breakpoints.down('sm')]: {
    width: '250px',
    height: '250px',
  },
}));

const PlanetCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha('#1E293B', 0.8)} 0%, 
    ${alpha(brandColors.secondary.main, 0.1)} 100%)`,
  backdropFilter: 'blur(20px)',
  border: `1px solid ${alpha(brandColors.secondary.main, 0.2)}`,
  borderRadius: '16px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    borderColor: brandColors.secondary.main,
    boxShadow: `0 10px 30px ${alpha(brandColors.secondary.main, 0.3)}`,
  },
}));

const HouseCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha('#1E293B', 0.6)} 0%, 
    ${alpha(brandColors.primary.main, 0.1)} 100%)`,
  backdropFilter: 'blur(20px)',
  border: `1px solid ${alpha(brandColors.primary.main, 0.2)}`,
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: brandColors.primary.main,
    boxShadow: `0 8px 25px ${alpha(brandColors.primary.main, 0.2)}`,
  },
}));

const TabPanel = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(3),
}));

const BirthChartVisualization: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedPlanet, setSelectedPlanet] = useState<string | null>(null);
  const [selectedHouse, setSelectedHouse] = useState<number | null>(null);
  console.log(isMobile);

  // Mock chart data (in real app, this would come from astrological calculation API)
  const chartData: ChartData = {
    personalInfo: {
      name: 'Sarah Chen',
      birthDate: 'June 15, 1995',
      birthTime: '2:30 PM',
      birthLocation: 'San Francisco, CA',
      sunSign: 'Gemini',
      moonSign: 'Scorpio',
      risingSign: 'Leo'
    },
    planets: [
      {
        name: 'Sun',
        sign: 'Gemini',
        degree: 24.5,
        house: 11,
        retrograde: false,
        element: 'Air',
        modality: 'Mutable'
      },
      {
        name: 'Moon',
        sign: 'Scorpio',
        degree: 18.2,
        house: 4,
        retrograde: false,
        element: 'Water',
        modality: 'Fixed'
      },
      {
        name: 'Mercury',
        sign: 'Cancer',
        degree: 12.8,
        house: 12,
        retrograde: true,
        element: 'Water',
        modality: 'Cardinal'
      },
      {
        name: 'Venus',
        sign: 'Taurus',
        degree: 6.3,
        house: 10,
        retrograde: false,
        element: 'Earth',
        modality: 'Fixed'
      },
      {
        name: 'Mars',
        sign: 'Virgo',
        degree: 29.1,
        house: 2,
        retrograde: false,
        element: 'Earth',
        modality: 'Mutable'
      },
      {
        name: 'Jupiter',
        sign: 'Sagittarius',
        degree: 15.7,
        house: 5,
        retrograde: false,
        element: 'Fire',
        modality: 'Mutable'
      },
      {
        name: 'Saturn',
        sign: 'Pisces',
        degree: 21.4,
        house: 8,
        retrograde: false,
        element: 'Water',
        modality: 'Mutable'
      },
      {
        name: 'Uranus',
        sign: 'Aquarius',
        degree: 3.9,
        house: 7,
        retrograde: false,
        element: 'Air',
        modality: 'Fixed'
      },
      {
        name: 'Neptune',
        sign: 'Capricorn',
        degree: 27.6,
        house: 6,
        retrograde: true,
        element: 'Earth',
        modality: 'Cardinal'
      },
      {
        name: 'Pluto',
        sign: 'Scorpio',
        degree: 1.2,
        house: 4,
        retrograde: false,
        element: 'Water',
        modality: 'Fixed'
      }
    ],
    houses: [
      {
        number: 1,
        sign: 'Leo',
        degree: 5.2,
        ruler: 'Sun',
        meaning: 'Self, Identity, First Impressions',
        planets: []
      },
      {
        number: 2,
        sign: 'Virgo',
        degree: 2.8,
        ruler: 'Mercury',
        meaning: 'Money, Values, Possessions',
        planets: ['Mars']
      },
      {
        number: 3,
        sign: 'Libra',
        degree: 28.4,
        ruler: 'Venus',
        meaning: 'Communication, Learning, Siblings',
        planets: []
      },
      {
        number: 4,
        sign: 'Scorpio',
        degree: 22.1,
        ruler: 'Mars',
        meaning: 'Home, Family, Roots',
        planets: ['Moon', 'Pluto']
      },
      {
        number: 5,
        sign: 'Sagittarius',
        degree: 18.7,
        ruler: 'Jupiter',
        meaning: 'Creativity, Romance, Children',
        planets: ['Jupiter']
      },
      {
        number: 6,
        sign: 'Capricorn',
        degree: 14.3,
        ruler: 'Saturn',
        meaning: 'Health, Work, Daily Routine',
        planets: ['Neptune']
      },
      {
        number: 7,
        sign: 'Aquarius',
        degree: 5.2,
        ruler: 'Uranus',
        meaning: 'Partnerships, Marriage, Open Enemies',
        planets: ['Uranus']
      },
      {
        number: 8,
        sign: 'Pisces',
        degree: 2.8,
        ruler: 'Neptune',
        meaning: 'Transformation, Shared Resources',
        planets: ['Saturn']
      },
      {
        number: 9,
        sign: 'Aries',
        degree: 28.4,
        ruler: 'Mars',
        meaning: 'Philosophy, Higher Learning, Travel',
        planets: []
      },
      {
        number: 10,
        sign: 'Taurus',
        degree: 22.1,
        ruler: 'Venus',
        meaning: 'Career, Reputation, Public Image',
        planets: ['Venus']
      },
      {
        number: 11,
        sign: 'Gemini',
        degree: 18.7,
        ruler: 'Mercury',
        meaning: 'Friends, Groups, Hopes and Dreams',
        planets: ['Sun']
      },
      {
        number: 12,
        sign: 'Cancer',
        degree: 14.3,
        ruler: 'Moon',
        meaning: 'Subconscious, Hidden Enemies, Spirituality',
        planets: ['Mercury']
      }
    ],
    aspects: [
      {
        planet1: 'Sun',
        planet2: 'Moon',
        aspect: 'Quincunx',
        degree: 150,
        orb: 6.3,
        type: 'major',
        nature: 'challenging'
      },
      {
        planet1: 'Venus',
        planet2: 'Jupiter',
        aspect: 'Trine',
        degree: 120,
        orb: 9.4,
        type: 'major',
        nature: 'harmonious'
      },
      {
        planet1: 'Mercury',
        planet2: 'Neptune',
        aspect: 'Opposition',
        degree: 180,
        orb: 14.8,
        type: 'major',
        nature: 'challenging'
      },
      {
        planet1: 'Mars',
        planet2: 'Saturn',
        aspect: 'Square',
        degree: 90,
        orb: 7.7,
        type: 'major',
        nature: 'challenging'
      },
      {
        planet1: 'Sun',
        planet2: 'Jupiter',
        aspect: 'Sextile',
        degree: 60,
        orb: 8.8,
        type: 'major',
        nature: 'harmonious'
      }
    ]
  };

  const zodiacSigns = [
    { name: 'Aries', symbol: '♈', element: 'Fire', color: '#FF6B6B' },
    { name: 'Taurus', symbol: '♉', element: 'Earth', color: '#4ECDC4' },
    { name: 'Gemini', symbol: '♊', element: 'Air', color: '#45B7D1' },
    { name: 'Cancer', symbol: '♋', element: 'Water', color: '#96CEB4' },
    { name: 'Leo', symbol: '♌', element: 'Fire', color: '#FFEAA7' },
    { name: 'Virgo', symbol: '♍', element: 'Earth', color: '#DDA0DD' },
    { name: 'Libra', symbol: '♎', element: 'Air', color: '#98D8C8' },
    { name: 'Scorpio', symbol: '♏', element: 'Water', color: '#F7DC6F' },
    { name: 'Sagittarius', symbol: '♐', element: 'Fire', color: '#BB8FCE' },
    { name: 'Capricorn', symbol: '♑', element: 'Earth', color: '#85C1E9' },
    { name: 'Aquarius', symbol: '♒', element: 'Air', color: '#F8C471' },
    { name: 'Pisces', symbol: '♓', element: 'Water', color: '#82E0AA' }
  ];

  const planetSymbols: { [key: string]: string } = {
    'Sun': '☉',
    'Moon': '☽',
    'Mercury': '☿',
    'Venus': '♀',
    'Mars': '♂',
    'Jupiter': '♃',
    'Saturn': '♄',
    'Uranus': '♅',
    'Neptune': '♆',
    'Pluto': '♇'
  };

  const getSignColor = (sign: string) => {
    const signData = zodiacSigns.find(s => s.name === sign);
    return signData ? signData.color : brandColors.primary.main;
  };

  const getElementColor = (element: string) => {
    switch (element) {
      case 'Fire': return '#FF6B6B';
      case 'Earth': return '#4ECDC4';
      case 'Air': return '#45B7D1';
      case 'Water': return '#96CEB4';
      default: return brandColors.primary.main;
    }
  };

  const getAspectColor = (nature: string) => {
    switch (nature) {
      case 'harmonious': return '#10B981';
      case 'challenging': return '#EF4444';
      default: return '#F59E0B';
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Chart wheel with houses and planets
  const renderChartWheel = () => {
    return (
      <ChartWheel>
        {/* Outer ring - Zodiac signs */}
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            border: `2px solid ${alpha(brandColors.primary.main, 0.5)}`,
          }}
        >
          {zodiacSigns.map((sign, index) => {
            const angle = (index * 30) - 90; // Start from top, each sign is 30 degrees
            const radian = (angle * Math.PI) / 180;
            const radius = 180;
            const x = Math.cos(radian) * radius;
            const y = Math.sin(radian) * radius;

            return (
              <Box
                key={sign.name}
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: `translate(${x}px, ${y}px) translate(-50%, -50%)`,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  background: alpha(sign.color, 0.2),
                  border: `1px solid ${alpha(sign.color, 0.5)}`,
                  fontSize: '16px',
                  fontWeight: 600,
                  color: sign.color,
                }}
              >
                {sign.symbol}
              </Box>
            );
          })}
        </Box>

        {/* Middle ring - Houses */}
        <Box
          sx={{
            position: 'absolute',
            width: '80%',
            height: '80%',
            borderRadius: '50%',
            border: `1px solid ${alpha(brandColors.secondary.main, 0.3)}`,
          }}
        >
          {chartData.houses.map((house) => {
            const angle = ((house.number - 1) * 30) - 90;
            const radian = (angle * Math.PI) / 180;
            const radius = 120;
            const x = Math.cos(radian) * radius;
            const y = Math.sin(radian) * radius;

            return (
              <Tooltip
                key={house.number}
                title={`House ${house.number}: ${house.meaning}`}
                arrow
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: `translate(${x}px, ${y}px) translate(-50%, -50%)`,
                    width: 24,
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    background: selectedHouse === house.number
                      ? alpha(brandColors.secondary.main, 0.3)
                      : alpha('#1E293B', 0.8),
                    border: `1px solid ${alpha(brandColors.secondary.main, 0.5)}`,
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'white',
                    cursor: 'pointer',
                    '&:hover': {
                      background: alpha(brandColors.secondary.main, 0.2),
                    }
                  }}
                  onClick={() => setSelectedHouse(house.number)}
                >
                  {house.number}
                </Box>
              </Tooltip>
            );
          })}
        </Box>

        {/* Inner ring - Planets */}
        <Box
          sx={{
            position: 'absolute',
            width: '60%',
            height: '60%',
            borderRadius: '50%',
            border: `1px solid ${alpha(brandColors.accent.main, 0.3)}`,
          }}
        >
          {chartData.planets.map((planet) => {
            // Calculate position based on house
            const angle = ((planet.house - 1) * 30) - 90 + (planet.degree / 30) * 30;
            const radian = (angle * Math.PI) / 180;
            const radius = 80;
            const x = Math.cos(radian) * radius;
            const y = Math.sin(radian) * radius;

            return (
              <Tooltip
                key={planet.name}
                title={`${planet.name} in ${planet.sign} (${planet.degree.toFixed(1)}°) - House ${planet.house}${planet.retrograde ? ' ℞' : ''}`}
                arrow
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: `translate(${x}px, ${y}px) translate(-50%, -50%)`,
                    width: 28,
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    background: selectedPlanet === planet.name
                      ? alpha(getSignColor(planet.sign), 0.3)
                      : alpha('#1E293B', 0.9),
                    border: `2px solid ${getSignColor(planet.sign)}`,
                    fontSize: '14px',
                    fontWeight: 700,
                    color: getSignColor(planet.sign),
                    cursor: 'pointer',
                    boxShadow: planet.retrograde
                      ? `0 0 8px ${alpha('#EF4444', 0.6)}`
                      : 'none',
                    '&:hover': {
                      background: alpha(getSignColor(planet.sign), 0.2),
                      transform: `translate(${x}px, ${y}px) translate(-50%, -50%) scale(1.1)`,
                    }
                  }}
                  onClick={() => setSelectedPlanet(planet.name)}
                >
                  {planetSymbols[planet.name]}
                </Box>
              </Tooltip>
            );
          })}
        </Box>

        {/* Center - Chart info */}
        <Box
          sx={{
            position: 'absolute',
            width: '30%',
            height: '30%',
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${alpha('#0F172A', 0.9)}, ${alpha(brandColors.primary.main, 0.1)})`,
            border: `2px solid ${alpha(brandColors.primary.main, 0.5)}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            p: 1,
          }}
        >
          <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '0.7rem' }}>
            {chartData.personalInfo.birthDate}
          </Typography>
          <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, fontSize: '0.8rem' }}>
            {t('birthChart.sunIn', { sign: chartData.personalInfo.sunSign })}
          </Typography>
          <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '0.6rem' }}>
            {t('birthChart.moonIn', { sign: chartData.personalInfo.moonSign })} {t('birthChart.risingIn', { sign: chartData.personalInfo.risingSign })} ↗
          </Typography>
        </Box>
      </ChartWheel>
    );
  };

  return (
    <ChartContainer>
      {/* Animated Background */}
      <Box sx={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        {Array.from({ length: 20 }).map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `twinkle ${2 + Math.random() * 3}s infinite`,
              animationDelay: `${Math.random() * 3}s`,
              '@keyframes twinkle': {
                '0%, 100%': { opacity: 0.3 },
                '50%': { opacity: 0.8 },
              },
            }}
          >
            <Star sx={{ fontSize: 6, color: 'white' }} />
          </Box>
        ))}
      </Box>

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                {t('birthChart.title')}
              </Typography>
              <Typography variant="h6" sx={{ color: '#94A3B8', mb: 2 }}>
                {chartData.personalInfo.name} • {chartData.personalInfo.birthDate} • {chartData.personalInfo.birthTime}
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748B' }}>
                📍 {chartData.personalInfo.birthLocation}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={t('birthChart.shareChart')}>
                <IconButton sx={{ color: '#94A3B8' }}>
                  <Share />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('birthChart.downloadPDF')}>
                <IconButton sx={{ color: '#94A3B8' }}>
                  <Download />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('birthChart.printChart')}>
                <IconButton sx={{ color: '#94A3B8' }}>
                  <Print />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Big Three */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <Card sx={{
                background: `linear-gradient(135deg, ${alpha('#FF6B6B', 0.2)}, ${alpha('#FF6B6B', 0.1)})`,
                border: `1px solid ${alpha('#FF6B6B', 0.3)}`,
                borderRadius: '12px'
              }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" sx={{ color: '#FF6B6B', mb: 1 }}>
                    ☉
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', mb: 0.5 }}>
                    {t('birthChart.sunIn', { sign: chartData.personalInfo.sunSign })}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#CBD5E1' }}>
                    {t('birthChart.coreIdentity')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{
                background: `linear-gradient(135deg, ${alpha('#96CEB4', 0.2)}, ${alpha('#96CEB4', 0.1)})`,
                border: `1px solid ${alpha('#96CEB4', 0.3)}`,
                borderRadius: '12px'
              }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" sx={{ color: '#96CEB4', mb: 1 }}>
                    ☽
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', mb: 0.5 }}>
                    {t('birthChart.moonIn', { sign: chartData.personalInfo.moonSign })}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#CBD5E1' }}>
                    {t('birthChart.emotionalNature')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{
                background: `linear-gradient(135deg, ${alpha('#FFEAA7', 0.2)}, ${alpha('#FFEAA7', 0.1)})`,
                border: `1px solid ${alpha('#FFEAA7', 0.3)}`,
                borderRadius: '12px'
              }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" sx={{ color: '#FFEAA7', mb: 1 }}>
                    ↗
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', mb: 0.5 }}>
                    {t('birthChart.risingIn', { sign: chartData.personalInfo.risingSign })}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#CBD5E1' }}>
                    {t('birthChart.firstImpression')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Main Content */}
        <Grid container spacing={4}>
          {/* Chart Wheel */}
          <Grid item xs={12} lg={6}>
            <Paper sx={{
              background: `linear-gradient(135deg, 
                ${alpha('#1E293B', 0.8)} 0%, 
                ${alpha(brandColors.primary.main, 0.05)} 100%)`,
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(brandColors.primary.main, 0.2)}`,
              borderRadius: '24px',
              p: 4,
              textAlign: 'center'
            }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'white' }}>
                {t('birthChart.natalChartWheel')}
              </Typography>
              {renderChartWheel()}

              {/* Legend */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Chip
                  size="small"
                  label={t('birthChart.legend.middle')}
                  sx={{
                    background: alpha(brandColors.secondary.main, 0.2),
                    color: brandColors.secondary.light
                  }}
                />
                <Chip
                  size="small"
                  label={t('birthChart.legend.inner')}
                  sx={{
                    background: alpha(brandColors.accent.main, 0.2),
                    color: brandColors.accent.light
                  }}
                />
                <Chip
                  size="small"
                  label={t('birthChart.legend.retrograde')}
                  sx={{
                    background: alpha('#EF4444', 0.2),
                    color: '#EF4444'
                  }}
                />
              </Box>
            </Paper>
          </Grid>

          {/* Detailed Information Tabs */}
          <Grid item xs={12} lg={6}>
            <Paper sx={{
              background: `linear-gradient(135deg, 
                ${alpha('#1E293B', 0.8)} 0%, 
                ${alpha(brandColors.secondary.main, 0.05)} 100%)`,
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(brandColors.secondary.main, 0.2)}`,
              borderRadius: '24px',
              overflow: 'hidden'
            }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  borderBottom: `1px solid ${alpha(brandColors.secondary.main, 0.2)}`,
                  '& .MuiTab-root': {
                    color: '#94A3B8',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&.Mui-selected': {
                      color: brandColors.secondary.light,
                    }
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: brandColors.secondary.main,
                  }
                }}
              >
                <Tab label={t('birthChart.tabs.planets')} />
                <Tab label={t('birthChart.tabs.houses')} />
                <Tab label={t('birthChart.tabs.aspects')} />
                <Tab label={t('birthChart.tabs.elements')} />
              </Tabs>

              {/* Planets Tab */}
              {activeTab === 0 && (
                <TabPanel>
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'white' }}>
                      {t('birthChart.planetaryPositions')}
                    </Typography>
                    <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                      {chartData.planets.map((planet) => (
                        <PlanetCard
                          key={planet.name}
                          sx={{
                            mb: 2,
                            cursor: 'pointer',
                            transform: selectedPlanet === planet.name ? 'scale(1.02)' : 'none',
                            borderColor: selectedPlanet === planet.name
                              ? brandColors.secondary.main
                              : alpha(brandColors.secondary.main, 0.2)
                          }}
                          onClick={() => setSelectedPlanet(planet.name)}
                        >
                          <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography
                                  variant="h5"
                                  sx={{
                                    color: getSignColor(planet.sign),
                                    fontWeight: 700,
                                    minWidth: 32,
                                    textAlign: 'center'
                                  }}
                                >
                                  {planetSymbols[planet.name]}
                                </Typography>
                                <Box>
                                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'white' }}>
                                    {t('birthChart.planetInSign', { planet: planet.name, sign: planet.sign })}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                                    {t('birthChart.planetDegreeHouse', { degree: planet.degree.toFixed(1), house: planet.house })}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Chip
                                  size="small"
                                  label={planet.element}
                                  sx={{
                                    background: alpha(getElementColor(planet.element), 0.2),
                                    color: getElementColor(planet.element),
                                    fontSize: '0.7rem'
                                  }}
                                />
                                {planet.retrograde && (
                                  <Chip
                                    size="small"
                                    label="℞"
                                    sx={{
                                      background: alpha('#EF4444', 0.2),
                                      color: '#EF4444',
                                      fontSize: '0.7rem',
                                      minWidth: 'auto',
                                      width: 24
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                          </CardContent>
                        </PlanetCard>
                      ))}
                    </Box>
                  </Box>
                </TabPanel>
              )}

              {/* Houses Tab */}
              {activeTab === 1 && (
                <TabPanel>
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'white' }}>
                      {t('birthChart.houseSystem')}
                    </Typography>
                    <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                      {chartData.houses.map((house) => (
                        <HouseCard
                          key={house.number}
                          sx={{
                            mb: 2,
                            cursor: 'pointer',
                            transform: selectedHouse === house.number ? 'scale(1.02)' : 'none',
                            borderColor: selectedHouse === house.number
                              ? brandColors.primary.main
                              : alpha(brandColors.primary.main, 0.2)
                          }}
                          onClick={() => setSelectedHouse(house.number)}
                        >
                          <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                              <Box sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                background: alpha(brandColors.primary.main, 0.2),
                                border: `1px solid ${brandColors.primary.main}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: brandColors.primary.light,
                                fontWeight: 700,
                                fontSize: '0.9rem'
                              }}>
                                {house.number}
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: 'white', mb: 0.5 }}>
                                  {house.sign} ({house.degree.toFixed(1)}°)
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#94A3B8', mb: 1 }}>
                                  {house.meaning}
                                </Typography>
                                {house.planets.length > 0 && (
                                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    {house.planets.map((planetName) => {
                                      const planet = chartData.planets.find(p => p.name === planetName);
                                      return (
                                        <Chip
                                          key={planetName}
                                          size="small"
                                          label={`${planetSymbols[planetName]} ${planetName}`}
                                          sx={{
                                            background: alpha(getSignColor(planet?.sign || ''), 0.2),
                                            color: getSignColor(planet?.sign || ''),
                                            fontSize: '0.7rem'
                                          }}
                                        />
                                      );
                                    })}
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </CardContent>
                        </HouseCard>
                      ))}
                    </Box>
                  </Box>
                </TabPanel>
              )}

              {/* Aspects Tab */}
              {activeTab === 2 && (
                <TabPanel>
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'white' }}>
                      {t('birthChart.majorAspects')}
                    </Typography>
                    <TableContainer sx={{ maxHeight: '400px' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ color: '#94A3B8', fontWeight: 600 }}>
                              {t('birthChart.table.planets')}
                            </TableCell>
                            <TableCell sx={{ color: '#94A3B8', fontWeight: 600 }}>
                              {t('birthChart.table.aspect')}
                            </TableCell>
                            <TableCell sx={{ color: '#94A3B8', fontWeight: 600 }}>
                              {t('birthChart.table.orb')}
                            </TableCell>
                            <TableCell sx={{ color: '#94A3B8', fontWeight: 600 }}>
                              {t('birthChart.table.type')}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {chartData.aspects.map((aspect, index) => (
                            <TableRow key={index}>
                              <TableCell sx={{ color: 'white' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2">
                                    {planetSymbols[aspect.planet1]} {aspect.planet1}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                                    —
                                  </Typography>
                                  <Typography variant="body2">
                                    {planetSymbols[aspect.planet2]} {aspect.planet2}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ color: 'white' }}>
                                <Chip
                                  size="small"
                                  label={aspect.aspect}
                                  sx={{
                                    background: alpha(getAspectColor(aspect.nature), 0.2),
                                    color: getAspectColor(aspect.nature),
                                    fontSize: '0.7rem'
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ color: '#94A3B8' }}>
                                {aspect.orb.toFixed(1)}°
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: getAspectColor(aspect.nature),
                                    fontWeight: 600,
                                    textTransform: 'capitalize'
                                  }}
                                >
                                  {aspect.nature}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </TabPanel>
              )}

              {/* Elements Tab */}
              {activeTab === 3 && (
                <TabPanel>
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'white' }}>
                      {t('birthChart.elements')}
                    </Typography>

                    {/* Element breakdown */}
                    {['Fire', 'Earth', 'Air', 'Water'].map((element) => {
                      const planetsInElement = chartData.planets.filter(p => p.element === element);
                      const percentage = (planetsInElement.length / chartData.planets.length) * 100;

                      return (
                        <Box key={element} sx={{ mb: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: 'white' }}>
                              {element}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                              {planetsInElement.length} planets ({percentage.toFixed(0)}%)
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: '100%',
                              height: 8,
                              background: alpha('#1E293B', 0.6),
                              borderRadius: 4,
                              overflow: 'hidden',
                              mb: 1
                            }}
                          >
                            <Box
                              sx={{
                                width: `${percentage}%`,
                                height: '100%',
                                background: getElementColor(element),
                                borderRadius: 4,
                                transition: 'width 0.5s ease'
                              }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {planetsInElement.map((planet) => (
                              <Chip
                                key={planet.name}
                                size="small"
                                label={`${planetSymbols[planet.name]} ${planet.name}`}
                                sx={{
                                  background: alpha(getElementColor(element), 0.2),
                                  color: getElementColor(element),
                                  fontSize: '0.7rem'
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      );
                    })}

                    <Divider sx={{ my: 3, borderColor: alpha(brandColors.secondary.main, 0.2) }} />

                    {/* Modality breakdown */}
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'white' }}>
                      {t('birthChart.tabs.modals')}
                    </Typography>

                    {['Cardinal', 'Fixed', 'Mutable'].map((modality) => {
                      const planetsInModality = chartData.planets.filter(p => p.modality === modality);
                      const percentage = (planetsInModality.length / chartData.planets.length) * 100;
                      const modalityColor = modality === 'Cardinal' ? '#FF6B6B' :
                        modality === 'Fixed' ? '#4ECDC4' : '#45B7D1';

                      return (
                        <Box key={modality} sx={{ mb: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: 'white' }}>
                              {modality}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                              {planetsInModality.length} planets ({percentage.toFixed(0)}%)
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: '100%',
                              height: 8,
                              background: alpha('#1E293B', 0.6),
                              borderRadius: 4,
                              overflow: 'hidden',
                              mb: 1
                            }}
                          >
                            <Box
                              sx={{
                                width: `${percentage}%`,
                                height: '100%',
                                background: modalityColor,
                                borderRadius: 4,
                                transition: 'width 0.5s ease'
                              }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {planetsInModality.map((planet) => (
                              <Chip
                                key={planet.name}
                                size="small"
                                label={`${planetSymbols[planet.name]} ${planet.name}`}
                                sx={{
                                  background: alpha(modalityColor, 0.2),
                                  color: modalityColor,
                                  fontSize: '0.7rem'
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </TabPanel>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Detailed Analysis Section */}
        <Box sx={{ mt: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 4, color: 'white' }}>
            {t('birthChart.detailedAnalysis')}
          </Typography>

          <Grid container spacing={3}>
            {/* Selected Planet Analysis */}
            {selectedPlanet && (
              <Grid item xs={12} md={6}>
                <Paper sx={{
                  background: `linear-gradient(135deg, 
                    ${alpha('#1E293B', 0.8)} 0%, 
                    ${alpha(brandColors.accent.main, 0.05)} 100%)`,
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${alpha(brandColors.accent.main, 0.2)}`,
                  borderRadius: '20px',
                  p: 4
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Typography variant="h3" sx={{ color: getSignColor(chartData.planets.find(p => p.name === selectedPlanet)?.sign || '') }}>
                      {planetSymbols[selectedPlanet]}
                    </Typography>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
                        {t(`birthChart.analysis.${selectedPlanet?.toLowerCase()}`)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                        {`${chartData.planets.find(p => p.name === selectedPlanet)?.sign} • House ${chartData.planets.find(p => p.name === selectedPlanet)?.house}`}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="body1" sx={{ color: '#CBD5E1', lineHeight: 1.6, mb: 3 }}>
                    {selectedPlanet && t(`birthChart.analysis.${selectedPlanet.toLowerCase()}`)}
                  </Typography>

                  <Button
                    variant="outlined"
                    sx={{
                      borderColor: brandColors.accent.main,
                      color: brandColors.accent.main,
                      '&:hover': {
                        borderColor: brandColors.accent.light,
                        background: alpha(brandColors.accent.main, 0.1)
                      }
                    }}
                    onClick={() => setSelectedPlanet(null)}
                  >
                    {t('birthChart.closeAnalysis')}
                  </Button>
                </Paper>
              </Grid>
            )}

            {/* Selected House Analysis */}
            {selectedHouse && (
              <Grid item xs={12} md={6}>
                <Paper sx={{
                  background: `linear-gradient(135deg, 
                    ${alpha('#1E293B', 0.8)} 0%, 
                    ${alpha(brandColors.primary.main, 0.05)} 100%)`,
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${alpha(brandColors.primary.main, 0.2)}`,
                  borderRadius: '20px',
                  p: 4
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Box sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: alpha(brandColors.primary.main, 0.2),
                      border: `2px solid ${brandColors.primary.main}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: brandColors.primary.light,
                      fontWeight: 700,
                      fontSize: '1.2rem'
                    }}>
                      {selectedHouse}
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
                        {t('birthChart.houseAnalysis', { number: selectedHouse })}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                        {chartData.houses.find(h => h.number === selectedHouse)?.meaning}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="body1" sx={{ color: '#CBD5E1', lineHeight: 1.6, mb: 3 }}>
                    {selectedHouse && t(`birthChart.analysis.house${selectedHouse}`)}
                  </Typography>

                  <Button
                    variant="outlined"
                    sx={{
                      borderColor: brandColors.primary.main,
                      color: brandColors.primary.main,
                      '&:hover': {
                        borderColor: brandColors.primary.light,
                        background: alpha(brandColors.primary.main, 0.1)
                      }
                    }}
                    onClick={() => setSelectedHouse(null)}
                  >
                    {t('birthChart.closeAnalysis')}
                  </Button>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* AI Insights Section */}
        <Box sx={{ mt: 6 }}>
          <Paper sx={{
            background: `linear-gradient(135deg, 
              ${alpha('#1E293B', 0.8)} 0%, 
              ${alpha(brandColors.secondary.main, 0.05)} 100%)`,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(brandColors.secondary.main, 0.2)}`,
            borderRadius: '24px',
            p: 4
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Psychology sx={{ fontSize: 32, color: brandColors.secondary.main }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
                {t('birthChart.aiInsights')}
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card sx={{
                  background: alpha('#0F172A', 0.6),
                  border: `1px solid ${alpha(brandColors.secondary.main, 0.3)}`,
                  borderRadius: '16px'
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <TrendingUp sx={{ color: '#10B981' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                        {t('birthChart.strengths')}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#CBD5E1', lineHeight: 1.6 }}>
                      Your Gemini Sun in the 11th house gives you natural networking abilities and intellectual leadership. The Scorpio Moon provides emotional depth and intuitive insights that complement your communicative nature.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{
                  background: alpha('#0F172A', 0.6),
                  border: `1px solid ${alpha('#F59E0B', 0.3)}`,
                  borderRadius: '16px'
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Info sx={{ color: '#F59E0B' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                        {t('birthChart.challenges')}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#CBD5E1', lineHeight: 1.6 }}>
                      Mercury retrograde in the 12th house may create challenges with clear communication of deep thoughts. The Mars-Saturn square suggests need for patience when pursuing goals.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{
                  background: alpha('#0F172A', 0.6),
                  border: `1px solid ${alpha(brandColors.accent.main, 0.3)}`,
                  borderRadius: '16px'
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <AutoAwesome sx={{ color: brandColors.accent.main }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                        {t('birthChart.growthAreas')}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#CBD5E1', lineHeight: 1.6 }}>
                      Focus on balancing your intellectual gifts with emotional intelligence. Your Venus-Jupiter trine offers opportunities for growth through relationships and creative expression.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Container>
    </ChartContainer>
  );
};

export default BirthChartVisualization;
